/**
 * Template off-screen para geração de PDF — multi-página sem cortar blocos.
 *
 * Suporta `containerId` único (vinda do useId no caller) para que múltiplas
 * instâncias na mesma página possam ser escopadas via CSS selector.
 */

import { ReactNode, useEffect, useRef, useState } from 'react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { sanitizeHtml } from '@/lib/sanitize'

export interface PDFRendererProps {
  title: string
  patientName: string
  subtitle?: string
  contentHtml: string
  extra?: ReactNode
  containerId?: string
}

const PAGE_W_MM = 210
const PAGE_H_MM = 297
const PAD_TOP_MM = 18
const PAD_BOTTOM_MM = 22
const PAD_X_MM = 16
const HEADER_H_MM = 26
const PATIENT_BLOCK_H_MM = 22
const FOOTER_H_MM = 14

const CONTENT_FIRST_PAGE_MM = PAGE_H_MM - PAD_TOP_MM - PAD_BOTTOM_MM - HEADER_H_MM - PATIENT_BLOCK_H_MM - FOOTER_H_MM
const CONTENT_OTHER_PAGES_MM = PAGE_H_MM - PAD_TOP_MM - PAD_BOTTOM_MM - HEADER_H_MM - FOOTER_H_MM

const MM_TO_PX = 3.7795275591
const CONTENT_FIRST_PX = CONTENT_FIRST_PAGE_MM * MM_TO_PX
const CONTENT_OTHER_PX = CONTENT_OTHER_PAGES_MM * MM_TO_PX

const PDF_STYLES = `
  .pdf-root { position: absolute; left: -10000px; top: 0; }
  .pdf-measure {
    position: absolute;
    left: -20000px;
    top: 0;
    width: ${PAGE_W_MM - PAD_X_MM * 2}mm;
    font-family: 'Plus Jakarta Sans', 'Segoe UI', sans-serif;
    font-size: 11px;
    line-height: 1.55;
    color: #1a1a1a;
  }
  .pdf-measure h3, .pdf-content h3 { font-size: 12px; font-weight: 700; color: hsl(76, 18%, 25%); text-transform: uppercase; letter-spacing: 0.05em; margin: 14px 0 6px; padding-bottom: 4px; border-bottom: 1px solid #e2e0d8; }
  .pdf-measure h3:first-child, .pdf-content h3:first-child { margin-top: 0; }
  .pdf-measure ul, .pdf-content ul { margin: 4px 0 8px 16px; padding: 0; }
  .pdf-measure li, .pdf-content li { margin: 3px 0; }
  .pdf-measure p, .pdf-content p { margin: 4px 0 8px; }
  .pdf-measure strong, .pdf-content strong { color: hsl(76, 18%, 25%); }
  .pdf-measure table, .pdf-content table { width: 100%; border-collapse: collapse; font-size: 10px; margin: 6px 0; }
  .pdf-measure th, .pdf-measure td, .pdf-content th, .pdf-content td { padding: 4px 6px; border-bottom: 1px solid #e2e0d8; text-align: left; }
  .pdf-measure th, .pdf-content th { background: #f6f5ef; font-weight: 700; }

  .pdf-page {
    width: ${PAGE_W_MM}mm;
    height: ${PAGE_H_MM}mm;
    box-sizing: border-box;
    padding: ${PAD_TOP_MM}mm ${PAD_X_MM}mm ${PAD_BOTTOM_MM}mm;
    background: #ffffff;
    color: #1a1a1a;
    font-family: 'Plus Jakarta Sans', 'Segoe UI', sans-serif;
    font-size: 11px;
    line-height: 1.55;
    position: relative;
    page-break-after: always;
    overflow: hidden;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .pdf-page:last-child { page-break-after: auto; }
  .pdf-header {
    display: flex; align-items: center; justify-content: space-between;
    padding-bottom: 10px;
    border-bottom: 2px solid hsl(76, 18%, 35%);
    margin-bottom: 14px;
  }
  .pdf-brand { display: flex; align-items: center; gap: 12px; }
  .pdf-brand img { height: 36px; width: auto; object-fit: contain; }
  .pdf-brand-text { display: flex; flex-direction: column; }
  .pdf-brand-name { font-size: 14px; font-weight: 700; color: hsl(76, 18%, 25%); letter-spacing: 0.04em; text-transform: uppercase; }
  .pdf-brand-sub { font-size: 10px; color: #5f6259; margin-top: 2px; }
  .pdf-meta { font-size: 10px; color: #5f6259; text-align: right; }
  .pdf-identificacao {
    background: #f6f5ef;
    border-left: 3px solid hsl(76, 18%, 35%);
    border-radius: 4px;
    padding: 10px 14px;
    margin-bottom: 14px;
  }
  .pdf-identificacao h2 { margin: 0 0 4px; font-size: 14px; font-weight: 700; color: hsl(76, 18%, 25%); }
  .pdf-identificacao p { margin: 0; font-size: 11px; }
  .pdf-pageinfo { font-size: 9px; color: #999; text-align: right; margin-bottom: 8px; }
  .pdf-content { }
  .pdf-footer {
    position: absolute;
    bottom: 10mm;
    left: ${PAD_X_MM}mm;
    right: ${PAD_X_MM}mm;
    padding-top: 8px;
    border-top: 1px solid #e2e0d8;
    font-size: 9px;
    color: #5f6259;
    text-align: center;
  }
`

function distributeBlocksInPages(measureNode: HTMLElement): string[] {
  const blocks = Array.from(measureNode.children) as HTMLElement[]
  if (blocks.length === 0) return ['']

  const pages: string[] = []
  let currentPageBlocks: string[] = []
  let currentHeight = 0
  let isFirstPage = true

  const maxHeightForPage = (firstPage: boolean) => (firstPage ? CONTENT_FIRST_PX : CONTENT_OTHER_PX)

  for (const block of blocks) {
    const blockHeight = block.offsetHeight
    const limit = maxHeightForPage(isFirstPage)

    if (currentHeight + blockHeight <= limit) {
      currentPageBlocks.push(block.outerHTML)
      currentHeight += blockHeight
    } else {
      pages.push(currentPageBlocks.join(''))
      isFirstPage = false
      currentPageBlocks = [block.outerHTML]
      currentHeight = blockHeight
    }
  }

  if (currentPageBlocks.length > 0) pages.push(currentPageBlocks.join(''))
  return pages.length > 0 ? pages : ['']
}

export function PDFRenderer({ title, patientName, subtitle, contentHtml, extra, containerId = 'pdf-root' }: PDFRendererProps) {
  const generatedAt = format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
  const measureRef = useRef<HTMLDivElement>(null)
  const [pagesHtml, setPagesHtml] = useState<string[]>([''])

  useEffect(() => {
    const node = measureRef.current
    if (!node) return
    let rafId = requestAnimationFrame(() => {
      rafId = requestAnimationFrame(() => {
        try {
          const distributed = distributeBlocksInPages(node)
          setPagesHtml(distributed)
        } catch (err) {
          console.error('Erro na distribuição de páginas do PDF:', err)
          setPagesHtml([contentHtml])
        }
      })
    })
    return () => cancelAnimationFrame(rafId)
  }, [contentHtml])

  return (
    <div id={containerId} className="pdf-root" data-pdf-container={containerId} aria-hidden="true">
      <style dangerouslySetInnerHTML={{ __html: PDF_STYLES }} />

      <div ref={measureRef} className="pdf-measure" dangerouslySetInnerHTML={{ __html: sanitizeHtml(contentHtml) }} />

      {pagesHtml.map((pageHtml, pageIdx) => (
        <div key={pageIdx} className="pdf-page">
          <div className="pdf-header">
            <div className="pdf-brand">
              <img src="/images/logos/logo-nina.png" alt="Mãe Nutrida" />
              <div className="pdf-brand-text">
                <span className="pdf-brand-name">Mãe Nutrida</span>
                <span className="pdf-brand-sub">{title}</span>
              </div>
            </div>
            <div className="pdf-meta">
              Emitido em {generatedAt}
              <br />
              Plataforma Mãe Nutrida · IA Nina
            </div>
          </div>

          {pageIdx === 0 && (
            <div className="pdf-identificacao">
              <h2>{patientName}</h2>
              {subtitle && <p>{subtitle}</p>}
            </div>
          )}

          {pageIdx === 0 && extra}

          {pagesHtml.length > 1 && <div className="pdf-pageinfo">Página {pageIdx + 1} de {pagesHtml.length}</div>}

          <div className="pdf-content" dangerouslySetInnerHTML={{ __html: sanitizeHtml(pageHtml) }} />

          <div className="pdf-footer">
            Este relatório é um apoio clínico gerado pela IA Nina com base na metodologia Mãe Nutrida. Não substitui avaliação individualizada.
            <br />
            maenutrida.com.br
          </div>
        </div>
      ))}
    </div>
  )
}
